import { Accordion, AccordionDetails, AccordionSummary, LinearProgress, Stack, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { FC } from "react";
import { useState } from "react";
import { useCustomerNotes } from "./useCustomerNotes";
import orderBy from "lodash/orderBy";
import type { NoteTS } from "../../components/noteStampedList/NoteStampedList";
import dayjs, { DATE_FORMAT, ISO_DATE_FORMAT } from "../../dayjs";
import ConfirmationDialog from "../../components/confirmationDialog/ConfirmationDialog";
import CustomerNotesList from "./CustomerNotesList";
import type { CustomerNoteCategory } from "./customerNotesApi";
import { CustomerNoteInput } from "./CustomerNoteInput";
import { useAuthContext } from "../../auth/AuthContext";

interface Props {
  readonly customerId: string;
  readonly category: CustomerNoteCategory;
  readonly title: string;
  readonly readonly?: boolean;
}

const NotesContent: FC<Omit<Props, "title">> = ({ customerId, category, readonly }) => {
  const { notes, isLoading, addNote, deleteNote } = useCustomerNotes(customerId, category);
  const { context } = useAuthContext();
  const canDeleteNotes =
    !readonly &&
    (context?.role === "ADMIN" || (context?.role === "AGENT" && context?.customPrivileges.canDeleteCustomerNotes));

  const [confirmationDialog, setConfirmationDialog] = useState<{
    content: string;
    onConfirm: () => Promise<void>;
  }>();

  const orderedNotes = orderBy(notes, (n) => n.date, ["desc"]);

  const handleAddNote = async (text: string, type: string | null) => {
    await addNote({
      text,
      type: type ?? undefined,
      category,
    });
  };

  const handleRemoveNote = async (idx: number, note: NoteTS) => {
    setConfirmationDialog({
      content: `Usunąć wpis '${note.text}' z ${note.date.format(DATE_FORMAT)}?`,
      onConfirm: async () => {
        await deleteNote(orderedNotes[idx].id);
        setConfirmationDialog(undefined);
      },
    });
  };
  return (
    <>
      {isLoading && <LinearProgress data-testid="loading-indicator" />}
      <Stack
        sx={{
          marginTop: "16px",
        }}
      >
        <CustomerNoteInput onSubmit={handleAddNote} disabled={readonly} />
        <CustomerNotesList
          values={orderedNotes.map((n) => ({ ...n, date: dayjs(n.date, ISO_DATE_FORMAT) }))}
          onRemove={canDeleteNotes ? handleRemoveNote : undefined}
        />
        <ConfirmationDialog
          open={!!confirmationDialog}
          title="Usuń notatkę"
          content={confirmationDialog?.content}
          onCancel={() => setConfirmationDialog(undefined)}
          onConfirm={async () => {
            await confirmationDialog?.onConfirm();
          }}
        />
      </Stack>
    </>
  );
};

const CustomerNotes: FC<Props> = ({ customerId, category, title, readonly }) => {
  return (
    <Accordion slotProps={{ transition: { unmountOnExit: true, mountOnEnter: true } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <NotesContent customerId={customerId} category={category} readonly={readonly} />
      </AccordionDetails>
    </Accordion>
  );
};

export default CustomerNotes;
